import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  Card,
  Statistic,
  Progress,
  Button,
  Select,
  DatePicker,
  Table,
  Tag,
  Space,
  Typography,
  Alert,
  Spin,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  CreditCardOutlined,
  UserOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { RootState, AppDispatch } from '../store';
import { fetchDashboardData, fetchBalance /* , fetchTransactionCharts */ } from '../store/slices/dashboardSlice';
import { DashboardData, Balance } from '../types';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { data, balance, charts, loading, balanceLoading, chartsLoading, error } = useSelector(
    (state: RootState) => state.dashboard
  );

  const [timeRange, setTimeRange] = useState<string>('30');
  const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month'>('day');

  useEffect(() => {
    // 获取仪表盘数据
    dispatch(fetchDashboardData(parseInt(timeRange)));
    dispatch(fetchBalance());
    // dispatch(fetchTransactionCharts({ period: chartPeriod })); // Disabled until backend supports it
  }, [dispatch, timeRange, chartPeriod]);

  const handleRefresh = () => {
    dispatch(fetchDashboardData(parseInt(timeRange)));
    dispatch(fetchBalance());
    // dispatch(fetchTransactionCharts({ period: chartPeriod })); // Disabled until backend supports it
  };

  // 统计卡片数据
  const statsCards: Array<{
    title: string;
    value: number;
    suffix?: string;
    precision?: number;
    icon: React.ReactNode;
    color: string;
    trend: number;
  }> = [
    {
      title: t('dashboard.totalRevenue'),
      value: data?.total_revenue || 0,
      suffix: 'USD',
      precision: 2,
      icon: <DollarOutlined />,
      color: '#16A34A',
      trend: data?.revenue_trend || 0,
    },
    {
      title: t('dashboard.totalOrders'),
      value: data?.total_orders || 0,
      icon: <ShoppingCartOutlined />,
      color: '#2F6BFF',
      trend: data?.orders_trend || 0,
    },
    {
      title: t('dashboard.successRate'),
      value: data?.success_rate || 0,
      suffix: '%',
      precision: 1,
      icon: <CreditCardOutlined />,
      color: '#2F6BFF',
      trend: data?.success_rate_trend || 0,
    },
    {
      title: t('dashboard.activeCustomers'),
      value: data?.active_customers || 0,
      icon: <UserOutlined />,
      color: '#F59E0B',
      trend: data?.customers_trend || 0,
    },
  ];

  // 最近交易表格列
  const transactionColumns = [
    {
      title: t('dashboard.transactionId'),
      dataIndex: 'id',
      key: 'id',
      render: (text: string) => (
        <Text code>{text ? `${text.substring(0, 8)}...` : '-'}</Text>
      ),
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-',
    },
    {
      title: t('common.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: any) => (
        <Text strong>
          {record.currency || ''} {((amount || 0) / 100).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          'completed': { color: 'success', text: t('dashboard.statusSucceeded') },
          'succeeded': { color: 'success', text: t('dashboard.statusSucceeded') },
          'partial_refunded': { color: 'warning', text: t('dashboard.statusPartialRefunded') },
          'refunded': { color: 'default', text: t('dashboard.statusRefunded') },
          'pending': { color: 'processing', text: t('dashboard.statusPending') },
          'requires_action': { color: 'processing', text: t('dashboard.statusRequiresAction') },
          'processing': { color: 'processing', text: t('dashboard.statusProcessing') },
          'failed': { color: 'error', text: t('dashboard.statusFailed') },
          'canceled': { color: 'default', text: t('dashboard.statusCanceled') },
          'cancelled': { color: 'default', text: t('dashboard.statusCanceled') },
        };
        // 未知状态直接显示原值，避免误标成"处理中"
        const config =
          statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: t('dashboard.time'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  // 图表颜色
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* 页面头部 */}
      <div className="page-header">
        <div className="header-content">
          <Title level={2}>{t('dashboard.title')}</Title>
          <Text type="secondary">{t('dashboard.welcome')}</Text>
        </div>
        <div className="header-actions">
          <Space>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: 120 }}
            >
              <Select.Option value="7">{t('dashboard.last7')}</Select.Option>
              <Select.Option value="30">{t('dashboard.last30')}</Select.Option>
              <Select.Option value="90">{t('dashboard.last90')}</Select.Option>
            </Select>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t('common.refresh')}
            </Button>
            <Button type="primary" icon={<DownloadOutlined />}>
              {t('common.exportReport')}
            </Button>
          </Space>
        </div>
      </div>

      {error && (
        <Alert
          message={t('dashboard.loadFailed')}
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsCards.map((card, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <Statistic
                title={card.title}
                value={card.value}
                precision={card.precision}
                suffix={card.suffix}
                valueStyle={{ 
                  color: card.color,
                  fontSize: '28px',
                }}
              />
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'center' }}>
                {card.icon}
                <span style={{ marginLeft: 8, fontSize: '14px' }}>
                  {card.trend > 0 ? (
                    <span style={{ color: '#16A34A' }}>
                      <ArrowUpOutlined /> +{card.trend.toFixed(1)}%
                    </span>
                  ) : (
                    <span style={{ color: '#ff4d4f' }}>
                      <ArrowDownOutlined /> {card.trend.toFixed(1)}%
                    </span>
                  )}
                  <Text type="secondary" style={{ marginLeft: 4 }}>
                    {t('dashboard.comparedToPrevious')}
                  </Text>
                </span>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 余额信息 */}
      {balance && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card title={t('dashboard.accountBalance')} loading={balanceLoading}>
              <Statistic
                title={t('dashboard.availableBalance')}
                value={(balance.available_balance || 0) / 100}
                suffix={balance.currency || 'USD'}
                precision={2}
                valueStyle={{ color: '#16A34A', fontSize: '32px' }}
              />
              <div style={{ marginTop: 16 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title={t('dashboard.pendingBalance')}
                      value={(balance.pending_balance || 0) / 100}
                      suffix={balance.currency || 'USD'}
                      precision={2}
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title={t('dashboard.frozenBalance')}
                      value={(balance.frozen_balance || 0) / 100}
                      suffix={balance.currency || 'USD'}
                      precision={2}
                      valueStyle={{ fontSize: '16px' }}
                    />
                  </Col>
                </Row>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title={t('dashboard.settlementOverview')} loading={balanceLoading}>
              {(() => {
                const avail = balance.available_balance || 0;
                const pend = balance.pending_balance || 0;
                const frozen = balance.frozen_balance || 0;
                const total = avail + pend + frozen;
                const pct = total > 0 ? Math.round((avail / total) * 100) : 0;
                const cur = balance.currency || 'USD';
                return (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <Text>{t('dashboard.settlableRatio')}</Text>
                      <Progress percent={pct} strokeColor="#16A34A" style={{ marginTop: 8 }} />
                    </div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Statistic
                          title={t('dashboard.pendingBalance')}
                          value={pend / 100}
                          suffix={cur}
                          precision={2}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title={t('dashboard.frozenBalance')}
                          value={frozen / 100}
                          suffix={cur}
                          precision={2}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Col>
                    </Row>
                  </>
                );
              })()}
            </Card>
          </Col>
        </Row>
      )}

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card
            title={t('dashboard.transactionTrend')}
            loading={chartsLoading}
            extra={
              <Select
                value={chartPeriod}
                onChange={setChartPeriod}
                style={{ width: 100 }}
              >
                <Select.Option value="day">{t('dashboard.byDay')}</Select.Option>
                <Select.Option value="week">{t('dashboard.byWeek')}</Select.Option>
                <Select.Option value="month">{t('dashboard.byMonth')}</Select.Option>
              </Select>
            }
          >
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={(data as any)?.trends?.daily_transactions || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => (v ? String(v).substring(5, 10) : '')}
                />
                <YAxis />
                <Tooltip
                  formatter={(value: any) => [(Number(value) / 100).toFixed(2), t('common.amount')]}
                />
                <Area
                  type="monotone"
                  dataKey="amount"
                  stroke="#2F6BFF"
                  fill="#2F6BFF"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={t('dashboard.paymentMethodDistribution')}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={(data as any)?.trends?.payment_methods || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="method"
                  label={(entry: any) => entry.method || t('dashboard.other')}
                >
                  {((data as any)?.trends?.payment_methods || []).map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* 最近交易 */}
      <Card
        title={t('dashboard.recentTransactions')}
        extra={
          <Button type="link" onClick={() => window.location.href = '/orders'}>
            {t('common.all')}
          </Button>
        }
      >
        <Table
          columns={transactionColumns}
          dataSource={data?.recent_transactions || []}
          pagination={false}
          size="middle"
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default DashboardPage; 